import React, { FC, useCallback, useEffect, useState } from 'react';

import { ArrowRightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { ADMIN_CHANGE_LOG_ROUTE } from 'routes/admin/list';
import { DASHBOARD_ROUTE } from 'routes/dashboard/list';
import { MY_EVENTS_ROUTE, REFERRAL_TEAM_ROUTE } from 'routes/events/list';
import { BRAND_ASSETS_ROUTE, FAQ_ROUTE, FEATURE_ROUTE } from 'routes/help/list';
import { TICKETS_ORDERS_ROUTE } from 'routes/tikets/list';
import { getPromoterEditRoute, getPromoterFollowerRoute } from 'routes/user-management/list';
import { t } from 'tools/i18n';
import { Icon, Layout, NavLink, Typography } from 'ui';

import { useLazyGetEmailEventsQuery } from 'services/events/eventsApiService';
import { setEditingEventId } from 'slices/eventsSlice';
import { setOpenedSider } from 'slices/systemSlice';
import { useAppDispatch } from 'store/index';

import { UserProfileType } from 'types/core/userTypes';

import s from '../AppNavigation.module.scss';
import AppNavigationUserInfo from '../app-navigation-user-info';
import { SOCIAL_NAVIGATIONS_CONFIG } from '../appNavigation.utils';

interface CoreNavigationTemplateProps {
  isOpenedSidebar: boolean;
  currentUser?: UserProfileType;
  apiVersion?: { versionNumber: string };
  handleLogout: () => void;
}

const CoreNavigationTemplate: FC<CoreNavigationTemplateProps> = (props) => {
  const { isOpenedSidebar, currentUser, handleLogout } = props;
  const [isShowAdminRoutes, setIsShowAdminRoutes] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [getEmailEvents, { isFetching }] = useLazyGetEmailEventsQuery();
  const dispatch = useAppDispatch();

  const generateHTML = (event) => `
        <td
          class="resTD"
          style='
            width: 33.33%;
            padding: 8px;
            text-align: center;
            vertical-align: top;
          '
        >
          <a
            href='${event.url}'
            style='text-decoration: none; color: white; cursor: pointer;'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              class='poster'
              src='${event.eventFlyer}'
              alt='${event.eventName}'
              style='
                max-width: 100%;
                border-radius: 8px;
                width: 100%;
                height : 254px;
                aspect-ratio: 0.707;
                object-fit: cover;
                display: block;
              '
            />
            <p
              class='res-date'
              style='
                text-align: left;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 100%;
                color: #7f86a4;
              '
            >
              ${event.dateFriendlyText}
            </p>
            <div
              class='dark-table-div res-event-title'
              style='
                color: #000;
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: 120%;
                letter-spacing: -0.32px;
                margin-top: 6px;
                margin-bottom: 6px;
                height: 50px;
                text-align: left;
              '
            >
              ${event.eventName}
            </div>
          </a>
        </td>
`;

  const exportCode = useCallback(async () => {
    const res = await getEmailEvents({ month: 7 });
    if ('data' in res) {
      const allHTML = res?.data?.categorized
        .map((category) => {
          const categoryHTML = category.events
            .reduce((acc, event, index) => {
              if (index % 3 === 0) acc.push([]);
              acc[acc.length - 1].push(generateHTML(event));
              return acc;
            }, [])
            .map((group) => {
              // Calculate how many empty <td> are needed
              const missingTds = 3 - group.length;

              // Add the necessary empty <td> elements with &nbsp;
              for (let i = 0; i < missingTds; i++) {
                group.push(`
              <td
                style="
                  width: 33.33%;
                  padding: 8px;
                  text-align: center;
                  vertical-align: top;
                "
              >
                &nbsp;
              </td>
            `);
              }

              return `
            <table width='100%' cellpadding='0' cellspacing='0' border='0' style='width: 100%; margin-left: auto; margin-right: auto; text-align: center; margin-bottom: 24px;'>
              <tbody>
                <tr>
                  ${group.join('\n')}
                </tr>
              </tbody>
            </table>
          `;
            })
            .join('\n');

          // Add the <h1> title before the events
          return `
         <div
          style="border-bottom: 1.5px solid rgb(206, 210, 229); width: 100%"
        ></div>
          <h1
            class="res-title"
            style="
              font-size: 24px;
              margin-top: 1rem;
              margin-bottom: 1rem;
              font-weight: 700;
              line-height: 34px;
              letter-spacing: -0.48px;
            "
          >
            ${category?.name}
          </h1>
          ${categoryHTML}`;
        })
        .join('\n');

      console.log(allHTML);
    }
  }, [getEmailEvents]);

  const handleCloseSidebar = () => {
    if (window.innerWidth < 767) {
      dispatch(setOpenedSider(false));
    }
    dispatch(setEditingEventId(''));
  };

  useEffect(() => {
    const roles = currentUser?.roles;
    const admin = roles?.find((role) => role.roleCode === 'ADM');

    if (currentUser && !admin) {
      setIsUserAdmin(false);
    } else if (currentUser && admin) {
      setIsUserAdmin(true);
    }
  }, [currentUser]);

  const NAVIGATIONS_CONFIG = [
    {
      to: MY_EVENTS_ROUTE,
      label: t('common_my_events'),
      icon: 'myEvents',
    },
    {
      to: REFERRAL_TEAM_ROUTE,
      label: t('common_referralTeam'),
      icon: 'users',
    },
    {
      to: TICKETS_ORDERS_ROUTE,
      label: t('common_tickets_orders'),
      icon: 'ticketOrders',
    },
    {
      to: DASHBOARD_ROUTE,
      label: t('common_reports'),
      icon: 'ticketReports',
    },
    {
      to: getPromoterFollowerRoute(
        currentUser?.promoters[0]?.promoterId ? currentUser?.promoters?.[0]?.promoterId : 'create',
      ),
      label: 'Promoter Followers',
      icon: 'user',
    },

    {
      to: getPromoterEditRoute(
        currentUser?.promoters[0]?.promoterId ? currentUser?.promoters?.[0]?.promoterId : 'create',
      ),
      label: t('common_organiser_details'),
      icon: 'house',
    },

    // {
    //   to: REFERRAL_LINK_ROUTE,
    //   label: 'Referral Link',
    //   icon: 'links',
    // },

    // {
    //   to: TICKETS_SCANS_ROUTE,
    //   label: t('common_tickets_scan'),
    //   icon: 'ticketScan',
    // },
    // {
    //   to: OUTLETS_ROUTE,
    //   label: t('common_outlets'),
    //   icon: 'ticketKiosk',
    // },
    // {
    //   to: SELLERS_ROUTE,
    //   label: t('common_sellers'),
    //   icon: 'ticketSeller',
    // },
  ];

  return (
    <Layout.Sider opened={isOpenedSidebar}>
      <div className={s.wrapper}>
        <div>
          <RouterNavLink className={s.logo} to={DASHBOARD_ROUTE}>
            <Icon name="logo" width={252} height={40} />
          </RouterNavLink>

          <AppNavigationUserInfo user={currentUser} isOpenedSidebar={isOpenedSidebar} />
          <div className={s.content}>
            {NAVIGATIONS_CONFIG.map((config) => (
              <button key={config.label} className={s.navLinkWrap} onClick={handleCloseSidebar}>
                <NavLink
                  label={config.label}
                  to={config.to}
                  typographyType="h5"
                  icon={config.icon}
                  className={s.link}
                />
              </button>
            ))}

            {isUserAdmin === true && (
              <>
                <button
                  className={cn(s.navLinkWrap, s.link, s.adminBtn, {
                    [s.active]: isShowAdminRoutes,
                  })}
                  onClick={() => setIsShowAdminRoutes((prev) => !prev)}>
                  <Icon size={18} className={s.icon} name="profile" />
                  Admin Tools
                </button>

                {isShowAdminRoutes && (
                  <>
                    <div className={s.adminRoutesWrapper}>
                      <NavLink
                        typographyType="h6"
                        label="Users List"
                        to="/admin/users"
                        className={s.adminLink}
                        eventCounter={currentUser?.events?.length || 0}
                      />
                    </div>
                    <div className={s.adminRoutesWrapper}>
                      <NavLink
                        label="Change Log"
                        typographyType="h6"
                        to={ADMIN_CHANGE_LOG_ROUTE}
                        className={s.adminLink}
                        eventCounter={currentUser?.events?.length || 0}
                      />
                    </div>
                    <div className={s.adminRoutesWrapper}>
                      <Button
                        type="link"
                        loading={isFetching}
                        className={s.exportCode}
                        onClick={exportCode}>
                        Export Email Code
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className={s.socialsLink}>
            {SOCIAL_NAVIGATIONS_CONFIG.map((item) => (
              <a key={item.label} href={item.to} className={s.socialsLinkItem} target="_blank">
                {item.label}
              </a>
            ))}
            <NavLink
              label="AVAILABLE TOOLS"
              typographyType="main"
              weight={700}
              to={FEATURE_ROUTE}
              className={s.additionalLinkItem}
              eventCounter={currentUser?.events?.length || 0}
            />
            <a
              href={BRAND_ASSETS_ROUTE}
              className={s.brandAssetsLinkItem}
              target="_blank"
              rel="noopener noreferrer">
              <Typography type="main" weight={700}>
                Brand Assets
              </Typography>
            </a>
            <NavLink
              label="Help & Faq"
              typographyType="main"
              weight={700}
              to={FAQ_ROUTE}
              className={s.additionalLinkItem}
              eventCounter={currentUser?.events?.length || 0}
            />
          </div>
          <button onClick={handleLogout} className={s.actions}>
            <Typography type="main" weight={700} className={s.logout}>
              <Icon name="logout" />
              {isOpenedSidebar && <span className={s.text}>{t('common_log_out')}</span>}
            </Typography>
          </button>
        </div>
        <div className={s.version}>
          <a href="https://www.showshappening.com" className={s.showshappening} target="_blank">
            <Icon name="shLogo" color="grey" size={20} />
            ShowsHappening.com
            <ArrowRightOutlined />
          </a>
          <div className={s.bannerButtons}>
            <a href="https://apps.apple.com/mt/app/showsmanager/id1591231466" target="_blank">
              <img src="/assets/images/apple.webp" alt="app store" width={84} />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.iu.showsmanager"
              target="_blank">
              <img src="/assets/images/google.webp" alt="google play" width={84} />
            </a>
          </div>
          <div style={{ textAlign: 'left', fontSize: 9 }}>
            ©{dayjs().year()} ShowsHappening Ltd. All rights reserved v2.1.1
          </div>
        </div>
      </div>
    </Layout.Sider>
  );
};

export default CoreNavigationTemplate;
